
import { mixins, Options } from 'vue-class-component'
import BaseFormDialogMixin from '@/components/common/mixins/BaseFormDialogMixin.vue'
import { Prop, Watch } from 'vue-property-decorator'
import MediaUploader from '@/components/media-manager/MediaUploader.vue'
import { IMedia, IPopup, IUploadFileResponse } from '@/utils/types'
import { constant } from '@/utils/constants'
import cloneDeep from 'lodash/cloneDeep'
import UploadingProgress from '@/components/common/ui/UploadingProgress.vue'
import UploadApi from '@/api/upload'
import CropperImageSelector from '@/components/tappable-area/selectors/CropperImageSelector.vue'
import CLoading from '@/components/common/ui/CLoading.vue'

@Options({
  components: { CLoading, CropperImageSelector, UploadingProgress, MediaUploader },
  emits: ['update:modelValue', 'update:addPopup', 'update:updatePopup'],
})
export default class PopupModal extends mixins(BaseFormDialogMixin) {
  @Prop()
  popupData!: IPopup

  form: IPopup = {
    _id: '',
    app_id: '',
    campaign_id: '',
    is_active: false,
    is_test_mode: false,
    display_id: '',
    popup_name: '',
    images: [],
    animation_type: constant.animation_type.fade_in,
  }

  errorImageMessage = ''
  file: File | null = null
  cropFiles: File[] = []
  cropperImageModalVisible = false
  loading = false

  get requiredRule() {
    return {
      popup_name: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.popup.popup_name'),
      }),
      messageImage: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.common.messages.image'),
      }),
    }
  }

  async onSelectMedia(file: IMedia) {
    this.errorImageMessage = ''
    if (file) {
      const imageSetting = {
        img_org: file.url ?? '',
        img_thumb: file.thumb ?? '',
        filename: file.name ?? '',
      }
      this.form.images = [
        {
          pc: imageSetting,
          sp: JSON.parse(JSON.stringify(imageSetting)),
        },
      ]
    }
  }

  onSubmit() {
    const isValid = this.validatePopup()
    this.$refs.popupForm
      .validate()
      .then(async (success: boolean) => {
        if (!success || !isValid) {
          return
        }
        if (this.isNew) {
          this.$emit('update:addPopup', this.form)
        } else {
          this.$emit('update:updatePopup', this.form)
        }
        this.visible = false
      })
      .catch((error: unknown) => {
        console.log('error', error)
      })
  }

  validatePopup() {
    this.errorImageMessage = ''
    if (!this.form.images || this.form.images.length === 0) {
      this.errorImageMessage = this.requiredRule.messageImage
      return false
    }

    return true
  }

  async validateInputFile(file: File) {
    this.errorImageMessage = ''

    if (!file && this.form.images.length === 0) {
      this.errorImageMessage = this.requiredRule.messageImage
      return false
    }

    if (file.size > constant.STORY_POPUP.MAX_FILE_SIZE) {
      this.errorImageMessage = this.$t('validate.file_too_big', {
        placeholder: file.name,
      })

      return false
    }

    let result = true
    if (file) {
      console.log(file)
      result = await this.validateImageSize(file)
    }

    return result
  }

  async validateImageSize(file: File) {
    return new Promise<boolean>((resolve) => {
      const img = new Image()
      img.src = window.URL.createObjectURL(file)
      img.onload = (event: Event) => {
        const data = event.target as HTMLImageElement

        if (data.width < constant.STORY_POPUP.MIN_IMAGE_WIDTH || data.width > constant.STORY_POPUP.MAX_IMAGE_WIDTH) {
          this.errorImageMessage = this.$t('validate.image_width_size', {
            placeholder: file.name,
          })
          resolve(false)
        }
        if (
          data.height < constant.STORY_POPUP.MIN_IMAGE_HEIGHT ||
          data.height > constant.STORY_POPUP.MAX_IMAGE_HEIGHT
        ) {
          this.errorImageMessage = this.$t('validate.image_height_size', {
            placeholder: file.name,
          })
          resolve(false)
        }
        resolve(true)
      }
    })
  }

  async submitFile(file: File) {
    try {
      const uploaded: IUploadFileResponse = await UploadApi.uploadFile(file)
      if (uploaded) {
        const imageSetting = {
          img_org: uploaded.file_url_org ?? '',
          img_thumb: uploaded.file_url_thumb ?? '',
          filename: uploaded.filename ?? '',
        }
        this.form.images = [
          {
            pc: imageSetting,
            sp: JSON.parse(JSON.stringify(imageSetting)),
          },
        ]
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  async onCropImage(file) {
    this.cropperImageModalVisible = false
    this.cropFiles.push(file)
    this.loading = true
    await this.submitFile(file)
    this.loading = false
    this.onCloseModal()
  }

  onCloseModal() {
    this.cropperImageModalVisible = false
    this.errorImageMessage = ''
    this.file = null
    this.cropFiles = []
  }

  @Watch('file')
  async handleUpload() {
    if (!this.file) {
      return false
    }

    try {
      if (!(await this.validateInputFile(this.file))) {
        await this.$q
          .dialog({
            title: this.$t('messages.confirm'),
            message: this.$t('messages.image_aspect_ratio_is_not_1_1', {
              ratio: '1:1',
            }),
            cancel: {
              flat: true,
              label: this.$t('messages.no'),
            },
            ok: {
              flat: true,
              label: this.$t('messages.yes'),
            },
            persistent: true,
          })
          .onOk(async () => {
            this.cropperImageModalVisible = true
          })
          .onCancel(async () => {
            this.onCloseModal()
          })
      } else {
        this.cropFiles.push(this.file)
        this.loading = true
        await this.submitFile(this.file)
        this.loading = false
        this.file = null
        this.cropFiles = []
      }
    } catch (error) {
      console.log(error)
    }
  }

  async created() {
    this.form = { ...this.form, ...cloneDeep(this.popupData) }
    this.parentForm = this.form as any
    this.initForm = cloneDeep(this.parentForm)
  }
}
